import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

export default function SearchSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/site-settings/search").then(r => setSettings(r.data));
  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    setSettings(next);
    setSaving(true);
    try { await api.put("/site-settings/search", next); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); load(); }
    finally { setSaving(false); }
  };

  const onDragEnd = (result) => {
    if (!result.destination || !settings) return;
    const arr = [...settings.groups];
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    persist({ ...settings, groups: arr });
  };
  const toggleEnabled = (key) => persist({
    ...settings, groups: settings.groups.map(g => g.key === key ? { ...g, enabled: !g.enabled } : g),
  });
  const renameLabel = (key, label) => persist({
    ...settings, groups: settings.groups.map(g => g.key === key ? { ...g, label } : g),
  });
  const changeMax = (v) => {
    const n = Math.max(1, Math.min(100, parseInt(v || "20", 10) || 20));
    persist({ ...settings, max_per_group: n });
  };
  const resetDefaults = () => {
    if (!confirm("Reset search groups to defaults? This will re-enable and re-label every group.")) return;
    persist({
      groups: [
        { key: "pathways",   label: "Pathways",   enabled: true },
        { key: "blogs",      label: "Blogs",      enabled: true },
        { key: "terms",      label: "Terms",      enabled: true },
      ],
      max_per_group: 20,
    });
  };

  if (!settings) return <div className="mt-6 text-slate-500">Loading…</div>;

  return (
    <Card className="border-slate-200 mt-6">
      <CardHeader>
        <CardTitle className="font-heading flex items-center justify-between">
          <span>Search settings</span>
          <span className="text-xs uppercase tracking-widest text-slate-400 font-normal">
            {saving ? "Saving…" : "Auto-saves"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-4">
          Choose which sections appear on the search results page, rename them, and set the order.
          Drag <GripVertical className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5"/> to reorder. Uncheck to hide a section from search results.
        </p>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="search-groups">
            {(provided) => (
              <ol data-testid="search-groups-list" ref={provided.innerRef} {...provided.droppableProps}
                  className="border border-slate-200 rounded-lg divide-y divide-slate-200">
                {settings.groups.map((g, i) => (
                  <Draggable key={g.key} draggableId={g.key} index={i}>
                    {(prov, snap) => (
                      <li ref={prov.innerRef} {...prov.draggableProps} data-testid={`search-group-row-${g.key}`}
                          className={`flex items-center gap-3 p-4 ${snap.isDragging ? "bg-sky-50 shadow-md" : "bg-white dark:bg-slate-900"} ${!g.enabled ? "opacity-60" : ""}`}>
                        <span {...prov.dragHandleProps} data-testid={`search-group-drag-${g.key}`}
                              className="text-slate-400 cursor-grab active:cursor-grabbing" title="Drag to reorder">
                          <GripVertical className="w-5 h-5"/>
                        </span>
                        <span className="text-xs font-mono text-slate-400 w-6">{String(i+1).padStart(2,"0")}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">{g.key}</div>
                          <Input data-testid={`search-group-label-${g.key}`} value={g.label} placeholder={g.key}
                            onChange={(e) => renameLabel(g.key, e.target.value)} maxLength={40} className="max-w-xs"/>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <span className="text-xs uppercase tracking-widest text-slate-500 hidden sm:inline">
                            {g.enabled ? "Enabled" : "Hidden"}
                          </span>
                          <input type="checkbox" data-testid={`search-group-toggle-${g.key}`}
                                 checked={g.enabled} onChange={() => toggleEnabled(g.key)}
                                 className="w-4 h-4 accent-sky-700"/>
                        </label>
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ol>
            )}
          </Droppable>
        </DragDropContext>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
          <label className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-slate-500">Max results per section:</span>
            <Input data-testid="search-max-per-group" type="number" min={1} max={100}
              value={settings.max_per_group} onChange={(e) => changeMax(e.target.value)} className="w-24"/>
          </label>
          <button data-testid="search-settings-reset" onClick={resetDefaults}
                  className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900">Reset to defaults</button>
        </div>
      </CardContent>
    </Card>
  );
}
